import type { ZoteroPaper } from "@/lib/desktop/zotero";

export type ZoteroPaperReadFailure = {
  paper: ZoteroPaper;
  message: string;
};

export type ZoteroPaperReadProgress = {
  completed: number;
  total: number;
};

export type ZoteroPaperReadResult = {
  imported: Array<{
    file: File;
    paper: ZoteroPaper;
  }>;
  failures: ZoteroPaperReadFailure[];
};

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "无法读取 Zotero PDF。";
}

export async function readZoteroPaperFiles(
  papers: ZoteroPaper[],
  readFile: (paper: ZoteroPaper) => Promise<File>,
  onProgress?: (progress: ZoteroPaperReadProgress) => void,
): Promise<ZoteroPaperReadResult> {
  const imported: ZoteroPaperReadResult["imported"] = [];
  const failures: ZoteroPaperReadFailure[] = [];

  for (let index = 0; index < papers.length; index += 1) {
    const paper = papers[index];
    try {
      imported.push({
        file: await readFile(paper),
        paper,
      });
    } catch (error) {
      failures.push({
        paper,
        message: errorMessage(error),
      });
    } finally {
      onProgress?.({
        completed: index + 1,
        total: papers.length,
      });
    }
  }

  return { imported, failures };
}

export function zoteroReadFailureSummary(
  failures: ZoteroPaperReadFailure[],
  importedCount: number,
) {
  const failedTitles = failures
    .slice(0, 3)
    .map(({ paper }) => paper.title || paper.filename)
    .filter(Boolean);
  const remaining = failures.length - failedTitles.length;
  const names = failedTitles.length
    ? `：${failedTitles.join("、")}${remaining > 0 ? ` 等 ${failures.length} 篇` : ""}`
    : "";

  if (importedCount > 0) {
    return `已读取 ${importedCount} 篇，${failures.length} 篇附件无法读取并已跳过${names}。`;
  }
  return `所选 ${failures.length} 篇 Zotero 附件均无法读取${names}。`;
}
